import { useMemo } from 'react';
import { useContext } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useElasticDivisionIds } from '@src/packages/utils';

import { SlidePanelContext } from 'packages/dashboard/components/SlidePanel/SlidePanelContext';

import { createBaseActionButtons } from '../actionButtons/baseActionButtons';
import { getChartAccess } from '../actionButtons/chartAccess';

/**
 * Hook to build widget content: subtitle, action buttons, and render content
 */
export const useWidgetContent = ({ widget, config, data, loading, additionalState, actions }) => {
  const { getSubTitle, getActionButtons, renderContent, additionalProps = {} } = config;
  const { id, filters } = widget;
  const locale = useSelector(({ settings }) => settings.locale);
  const intl = useIntl();
  const { boardSelected } = useContext(SlidePanelContext);
  const orgId = useSelector(state => state.organizations.organization.id);
  /* Role-aware query scope, same as useWidgetData: the action buttons below turn this
   * into the divisionIds of the export request. */
  const queryDivisionIds = useElasticDivisionIds();

  // Build subtitle
  const subTitle = useMemo(() => {
    if (!getSubTitle) return null;
    return getSubTitle({ data, widget, intl, ...additionalProps });
  }, [getSubTitle, data, widget, intl, additionalProps]);

  // Build action buttons
  const actionButtons = useMemo(() => {
    if (getActionButtons) {
      return getActionButtons({
        handleEdit: actions.handleEdit,
        handleDelete: actions.handleDelete,
        handleDownload: actions.handleDownload,
        setDownloading: actions.setDownloading,
        loading,
        downloading: actions.downloading,
        data,
        queryParams: widget.queryParams,
        intl,
        widget,
        locale,
        filters: widget.filters,
        additionalProps: {
          ...additionalProps,
          orgId,
          queryDivisionIds,
          boardSelected,
        },
      });
    }

    // Default action buttons using helper
    return createBaseActionButtons({
      handleEdit: actions.handleEdit,
      handleDelete: actions.handleDelete,
      handleDownload: actions.handleDownload,
      downloading: actions.downloading,
      ...getChartAccess(boardSelected),
      iconSize: 'sm',
    });
  }, [
    getActionButtons,
    actions.handleEdit,
    actions.handleDownload,
    actions.setDownloading,
    actions.downloading,
    actions.handleDelete,
    additionalProps,
    loading,
    data,
    widget,
    intl,
    locale,
    orgId,
    queryDivisionIds,
    boardSelected,
  ]);

  // Build render content
  const content = useMemo(() => {
    if (!renderContent) return null;
    return renderContent({
      data,
      loading,
      widgetId: id,
      ref: actions.ref,
      widget,
      filters,
      additionalState,
      ...additionalProps,
    });
  }, [
    renderContent,
    data,
    loading,
    id,
    actions.ref,
    widget,
    filters,
    additionalState,
    additionalProps,
  ]);

  return {
    subTitle,
    actionButtons,
    content,
  };
};
